import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";

export default function CommissionThanks() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 6;
  const INTERVAL_MS = 2500;

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/payments/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      } catch {
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <main className="relative max-w-2xl mx-auto px-6 py-24 text-center">
      <div className="hero-card rounded-sm px-10 py-16">
        {status === "polling" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Confirming your reservation</div>
            <h1 className="font-serif-display text-3xl text-[var(--rb-text-heading)] mb-6">Just a moment ✦</h1>
            <p className="font-serif-display italic text-[var(--rb-text-body)]">Verifying your payment with Stripe…</p>
            <div className="mt-8 flex justify-center"><Spinner /></div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Your spot is held ✦</div>
            <h1 className="font-serif-display text-4xl text-[var(--rb-text-heading)] mb-6">
              Thank you, with all my heart.
            </h1>
            <p className="font-serif-display italic text-[18px] text-[var(--rb-text-body)] leading-relaxed">
              Your deposit of <span className="text-[var(--rb-gold)]">${((data?.amount_total || 0) / 100).toFixed(2)}</span> has
              been received. A confirmation is on its way to your email, and I&apos;ll personally reach out
              within a day or two to begin shaping your diorama.
            </p>
            <div className="ornament mt-8 mb-8"><span>✦</span></div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/gallery" className="btn-gold">Visit the Garden</Link>
              <Link to="/" className="btn-ghost">Return home</Link>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">The session expired</div>
            <h1 className="font-serif-display text-3xl text-[var(--rb-text-heading)] mb-6">No payment was taken.</h1>
            <p className="font-serif-display italic text-[var(--rb-text-body)] mb-8">You can try again whenever you&apos;re ready.</p>
            <Link to="/commission" className="btn-gold">Try again</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Hmm…</div>
            <h1 className="font-serif-display text-3xl text-[var(--rb-text-heading)] mb-6">We couldn&apos;t confirm yet.</h1>
            <p className="font-serif-display italic text-[var(--rb-text-body)] mb-8">
              Don&apos;t worry — if your card was charged, we&apos;ll see it on our side and reach out.
              Otherwise, just try again.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/commission" className="btn-gold">Back to commission</Link>
              <Link to="/" className="btn-ghost">Return home</Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function Spinner() {
  return (
    <svg width="40" height="40" viewBox="0 0 50 50" className="animate-spin">
      <circle cx="25" cy="25" r="20" stroke="#F2E3C6" strokeWidth="4" fill="none" />
      <circle cx="25" cy="25" r="20" stroke="#D4AF37" strokeWidth="4" fill="none"
              strokeLinecap="round" strokeDasharray="80 200" />
    </svg>
  );
}
